import { createHistogramBars, isScoringType, getDefaultScoringFunction, getColor, initScoringFunctionsParams, SCORING_FUNCTIONS_RULES, DesirabilityFunctionType } from '@discngine/moosa-models';
import 'react';
import 'react-redux';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { v4 } from 'uuid';

const selectTableInfo = (state) => state.tableInfo;
const selectTableId = (state) => state.tableInfo.id;
createSelector(selectTableInfo, (tableInfo) => tableInfo.siderWidth);

function toColumnMetaInfo(fields) {
    return fields.map((field) => {
        const histogramMax = Math.max(...(field.statistics?.histogram || [])) || 1; // todo temporary fix, wait for BE
        let histogramBars = [];
        let dataWidth = 0;
        const isDiscreteColumn = field.statistics?.textCategories !== null;
        if (field.statistics && field.statistics.histogram) {
            const min = field.statistics.min;
            const max = field.statistics.max;
            dataWidth = max - min;
            histogramBars = createHistogramBars(field.statistics);
        }
        const meta = {
            ...field,
            histogramMax,
            histogramBars,
            dataWidth,
            isDiscreteColumn,
        };
        return meta;
    });
}

const DEFAULT_SIDER_WIDTH = 355;
const MAX_SIDER_WIDTH = DEFAULT_SIDER_WIDTH * 2;
const initialState$1 = {
    id: '',
    name: '',
    isLoading: false,
    tableDataSize: 0,
    numRows: 0,
    numColumns: 0,
    columns: [],
    columnsMap: {},
    columnIds: [],
    columnIndexes: {},
    scoringTemplateId: null,
    comparisonScoringTemplateId: null,
    isTemplatesComparisonMode: false,
    missingValues: 0,
    siderWidth: DEFAULT_SIDER_WIDTH,
};
const tableInfoSlice = createSlice({
    name: 'tableInfo',
    initialState: initialState$1,
    reducers: {
        initTableInfo(state, action) {
            const { data } = action.payload;
            state.id = data._id;
            state.name = data.fileName;
            state.tableDataSize = data.fileSize;
            state.numRows = data.rowCount;
            state.numColumns = data.fields.length;
            state.columns = toColumnMetaInfo(data.fields);
            state.columnIds = data.fields.map((field) => field.name);
            state.comparisonScoringTemplateId = null;
            state.isTemplatesComparisonMode = false;
            state.columnsMap = state.columns.reduce((acc, col) => {
                acc[col.name] = col;
                return acc;
            }, {});
            state.columnIndexes = state.columns.reduce((acc, col, index) => {
                acc[col.name] = index;
                return acc;
            }, {});
            // TODO need to discuss
            state.scoringTemplateId = {
                parentId: data.scoringTemplateId,
                id: data.scoringTemplateId,
            };
            state.missingValues = data.missingValues;
        },
        toggleTableInfoLoading(state, action) {
            state.isLoading = action.payload;
        },
        setIsTemplatesComparisonMode(state, action) {
            state.isTemplatesComparisonMode = action.payload;
        },
        setTableScoringTemplateId(state, action) {
            state.scoringTemplateId = action.payload
                ? { parentId: action.payload.parentId, id: action.payload.id }
                : null;
        },
        setTableComparisonScoringTemplateId(state, action) {
            state.comparisonScoringTemplateId = action.payload
                ? { parentId: action.payload.parentId, id: action.payload.id }
                : null;
        },
        setSiderWidth(state, action) {
            let width = action.payload;
            if (width > MAX_SIDER_WIDTH) {
                width = MAX_SIDER_WIDTH;
            }
            else if (width < DEFAULT_SIDER_WIDTH) {
                width = DEFAULT_SIDER_WIDTH;
            }
            state.siderWidth = width;
        },
    },
});
const { initTableInfo, toggleTableInfoLoading, setTableScoringTemplateId, setTableComparisonScoringTemplateId, setIsTemplatesComparisonMode, setSiderWidth, } = tableInfoSlice.actions;

const DEFAULT_TEMPLATE_SIZE = 5;
function columnsToScoringFunctions(fields) {
    const numberFields = fields
        .filter((val) => isScoringType(val.type, val.isDiscreteColumn))
        .slice(0, DEFAULT_TEMPLATE_SIZE);
    const allMetadata = toColumnMetaInfo(numberFields); // todo rewrite without this conversion
    const scoringFunction = allMetadata.reduce((acc, metadata) => {
        acc[metadata.name] = getDefaultScoringFunction(metadata);
        return acc;
    }, {});
    return scoringFunction;
}

const selectScoringTemplate = (state) => state.scoringTemplate;
const selectScoringFunction = (state) => state.scoringTemplate.func;
const selectScoringOpenPropertyPanels = (state) => state.scoringTemplate.openPropertyPanels;
const selectScoringColumnOrder = (state) => state.scoringTemplate.order;
const selectScoringTemplateChanged = (state) => state.scoringTemplate.changed;
const selectScoringFunctionByColumn = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column]);
const selectPropertyPanelOpen = (column) => createSelector((state) => state.scoringTemplate, (scoringTemplate) => !!scoringTemplate.openPropertyPanels[column]);
const selectScoringWeight = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column].weight);
const selectMissingValue = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column].missingValue?.replaceWith);
const selectMeasurementError = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column].measurementError);
const selectErrorsAvailable = (state) => Object.values(state.scoringTemplate.func).some((col) => col.measurementError.type !== 'none');
const selectMissingValueY = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column].missingValue?.replaceWithY);
const selectMissingValueReplacementColumn = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column].missingValue?.replaceWithColumn);
const selectFilterFrom = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column].filter?.from);
const selectFilterTo = (column) => createSelector((state) => state.scoringTemplate.func, (func) => func[column].filter?.to);
const selectCustomFunctions = (state) => state.scoringTemplate.customFunctions;
const selectScoringTemplateData = createSelector(selectScoringColumnOrder, selectScoringFunction, (order, func) => ({ order, func }));

const colorGenerator = getColor();
var DesirabilityFunctionLineSerialization;
(function (DesirabilityFunctionLineSerialization) {
    DesirabilityFunctionLineSerialization["LINE"] = "line";
    DesirabilityFunctionLineSerialization["PARAMS"] = "params";
})(DesirabilityFunctionLineSerialization || (DesirabilityFunctionLineSerialization = {}));
const convertTemplateToTableDataType = (template, columnMap) => {
    let func = {};
    let order = [];
    if (template) {
        template.columns.forEach(({ columnName, color, isVisible, weight, filter, missingValue, measurementError, desirability: { points, type, name }, isDiscreteStringFunction, }) => {
            const metadata = columnMap[columnName];
            let functionParams = {};
            if (isScoringType(metadata?.type, columnMap[columnName]?.isDiscreteColumn)) {
                functionParams = initScoringFunctionsParams(metadata);
            }
            functionParams[type] = SCORING_FUNCTIONS_RULES[type].fromServerTemplate(points.map((point) => ({ ...point, id: v4() })), metadata);
            func[columnName] = {
                column: columnName,
                color: color ?? colorGenerator.next().value,
                isInUse: isVisible,
                weight,
                type,
                name,
                functionParams,
                missingValue,
                filter,
                measurementError: measurementError || { type: 'none' },
                isDiscreteStringFunction: metadata?.isDiscreteColumn && isDiscreteStringFunction,
            };
            order.push(columnName);
        });
    }
    return { func, order };
};
function saveDesirabilityFunction(createDesirabilityFunction, name, property) {
    return async () => {
        const payload = desirabilityFunctionToPayload(name, property);
        await createDesirabilityFunction(payload);
    };
}
function scoringFunctionToPayload(name, template, allMetadata = null, pointsSerialization = DesirabilityFunctionLineSerialization.PARAMS) {
    let columns = template?.order;
    if (pointsSerialization === DesirabilityFunctionLineSerialization.LINE) {
        columns = columns?.filter((col) => allMetadata && allMetadata[col]);
    }
    const columnsData = columns
        .map((col) => template.func[col])
        .map((property) => {
        const rules = SCORING_FUNCTIONS_RULES[property.type];
        const params = property.functionParams[property.type];
        let points;
        switch (pointsSerialization) {
            case DesirabilityFunctionLineSerialization.LINE:
                const metadata = allMetadata[property.column];
                if (property.type === DesirabilityFunctionType.discrete) {
                    points = params.points
                        .filter((pointF) => !pointF?.isNotInFunction)
                        .map((point) => ({ x: point.x, y: point.y }));
                }
                else {
                    const range = SCORING_FUNCTIONS_RULES[property.type].getRange(params, metadata);
                    //@ts-ignore
                    points = rules.toLine(params, range).map(({ x, y }) => ({ x, y }));
                }
                break;
            case DesirabilityFunctionLineSerialization.PARAMS:
                points = rules.toServerTemplate(params);
                break;
            default:
                throw new Error();
        }
        const item = {
            color: property.color,
            columnName: property.column,
            isVisible: property.isInUse,
            weight: property.weight,
            desirability: {
                type: property.type,
                name: property.name,
                points,
            },
            missingValue: property.missingValue,
            measurementError: property.measurementError,
            filter: property.filter,
            isDiscreteStringFunction: property.isDiscreteStringFunction,
        };
        return item;
    });
    return {
        name,
        columns: columnsData,
    };
}
function desirabilityFunctionToPayload(name, property) {
    const { functionParams, type, isDiscreteStringFunction } = property;
    const rules = SCORING_FUNCTIONS_RULES[type];
    const params = functionParams[type];
    const points = rules.toServerTemplate(params);
    return {
        name,
        points,
        type,
        isDiscreteStringFunction,
    };
}

const initialState = {
    func: {},
    changed: false,
    order: [],
    openPropertyPanels: {},
    customFunctions: [],
};
const scoringTemplateSlice = createSlice({
    name: 'scoringTemplate',
    initialState,
    reducers: {
        setScoringWeight(state, { payload: { col, weight } }) {
            state.func[col].weight = weight;
            state.changed = true;
        },
        toggleColumnInScoring(state, action) {
            state.func[action.payload.col].isInUse = action.payload.isInUse;
            state.changed = true;
        },
        changeDesirabilityFunction(state, action) {
            const { column, desirability } = action.payload;
            state.func[column] = desirability;
            state.changed = true;
        },
        changeDesirabilityParams(state, action) {
            const { column, type, params } = action.payload;
            state.func[column].functionParams[type] = params;
            state.changed = true;
        },
        setFromSavedTemplate(state, action) {
            const { template, columnMap } = action.payload;
            const convertedTemplate = convertTemplateToTableDataType(template, columnMap);
            state.func = convertedTemplate.func;
            state.order = convertedTemplate.order;
            state.changed = false;
        },
        templateSaved(state) {
            state.changed = false;
        },
        changeColumnOrder(state, action) {
            const { fromColumn, toColumn } = action.payload;
            const order = state.order;
            const fromIndex = order.indexOf(fromColumn);
            const toIndex = order.indexOf(toColumn);
            const item = order.splice(fromIndex, 1)[0];
            order.splice(toIndex, 0, item);
            state.changed = true;
        },
        addColumnToScoring(state, action) {
            const { colId, metadata } = action.payload;
            state.func[colId] = getDefaultScoringFunction(metadata);
            state.order.unshift(colId);
            state.openPropertyPanels[colId] = 1;
            state.changed = true;
        },
        removeColumnFromScoring(state, action) {
            const colId = action.payload;
            const index = state.order.indexOf(colId);
            state.order.splice(index, 1);
            delete state.func[colId];
            delete state.openPropertyPanels[colId];
            state.changed = true;
        },
        initFromTableMetadata(state, action) {
            state.func = {};
            state.order = [];
            state.openPropertyPanels = {};
            state.changed = false;
        },
        resetToDefault(state) {
            state.func = {};
            state.order = [];
            state.openPropertyPanels = {};
            state.changed = false;
        },
        setColumnColor(state, { payload: { column, color } }) {
            state.func[column].color = color;
            state.changed = true;
        },
        toggleColumnPropertyOpen(state, { payload: { column, open } }) {
            if (open) {
                state.openPropertyPanels[column] = 1;
            }
            else {
                delete state.openPropertyPanels[column];
            }
        },
        movePoint(state, { payload: { column, type, point }, }) {
            const params = state.func[column].functionParams[type];
            // @ts-ignore
            state.func[column].functionParams[type] = SCORING_FUNCTIONS_RULES[type].movePoint(params, point);
            state.changed = true;
        },
        addPoint(state, { payload: { column, type, point, fieldType, xSort, ySort }, }) {
            const params = state.func[column].functionParams[type];
            // @ts-ignore
            state.func[column].functionParams[type] = SCORING_FUNCTIONS_RULES[type].addPoint(params, { ...point, id: v4() }, fieldType, xSort, ySort);
            state.changed = true;
        },
        removePoint(state, { payload: { column, type, index }, }) {
            const params = state.func[column].functionParams[type];
            // @ts-ignore
            state.func[column].functionParams[type] = SCORING_FUNCTIONS_RULES[type].removePoint(params, index);
            state.changed = true;
        },
        changeDiscretePointsOrder(state, action) {
            const { fromPoint, toPoint, columnName } = action.payload;
            const points = state.func[columnName].functionParams[DesirabilityFunctionType.discrete].points;
            const fromIndex = points.findIndex((point) => point.x === fromPoint);
            const toIndex = points.map((point) => point.x).indexOf(toPoint);
            const item = points.splice(fromIndex, 1)[0];
            points.splice(toIndex, 0, item);
            state.changed = true;
        },
        setMissingValue(state, action) {
            const { colId, replaceWith, replaceWithY: y, replaceWithColumn, isDiscreteStringFunction, } = action.payload;
            const hasX = typeof replaceWith === 'number' ||
                (isDiscreteStringFunction && typeof replaceWithColumn !== 'string');
            const hasY = typeof y === 'number';
            const hasColumn = typeof replaceWithColumn === 'string';
            if (hasX && hasY) {
                throw Error('Both replaceWith and replaceWithY were set');
            }
            let replaceWithY = y;
            if (hasY) {
                if (y < 0) {
                    replaceWithY = 0;
                }
                else if (y > 1) {
                    replaceWithY = 1;
                }
            }
            if (hasX || hasY || hasColumn) {
                state.func[colId].missingValue = { replaceWith, replaceWithY, replaceWithColumn };
            }
            else {
                delete state.func[colId].missingValue;
            }
            state.changed = true;
        },
        setErrorMode(state, action) {
            state.func[action.payload.colId].measurementError = action.payload.measurementError;
            state.changed = true;
        },
        setFilterFrom(state, action) {
            const { colId, from } = action.payload;
            let filter = state.func[colId].filter;
            if (!filter) {
                filter = {};
                state.func[colId].filter = filter;
            }
            if (typeof from === 'number') {
                filter.from = from;
            }
            else {
                delete filter.from;
            }
            state.changed = true;
        },
        setFilterTo(state, action) {
            const { colId, to } = action.payload;
            let filter = state.func[colId].filter;
            if (!filter) {
                filter = {};
                state.func[colId].filter = filter;
            }
            if (typeof to === 'number') {
                filter.to = to;
            }
            else {
                delete filter.to;
            }
            state.changed = true;
        },
        reassignColumn(state, action) {
            const { oldColumn, newColumn, metadata } = action.payload;
            const index = state.order.indexOf(oldColumn);
            state.func[newColumn] = getDefaultScoringFunction(metadata);
            delete state.func[oldColumn];
            state.order[index] = newColumn;
            if (state.openPropertyPanels[oldColumn]) {
                state.openPropertyPanels[newColumn] = 1;
                delete state.openPropertyPanels[oldColumn];
            }
            state.changed = true;
        },
        setCustomFunctions(state, action) {
            state.customFunctions = action.payload.functions;
        },
        setScoringTemplateState(state, action) {
            state.func = action.payload.templateState.func;
            state.changed = action.payload.templateState.changed;
            state.order = action.payload.templateState.order;
            state.openPropertyPanels = action.payload.templateState.openPropertyPanels;
            state.customFunctions = action.payload.templateState.customFunctions;
        },
    },
});
const { setScoringWeight, toggleColumnInScoring, changeDesirabilityFunction, changeDesirabilityParams, setFromSavedTemplate, templateSaved, addColumnToScoring, removeColumnFromScoring, changeColumnOrder, initFromTableMetadata, resetToDefault, setColumnColor, toggleColumnPropertyOpen, movePoint, addPoint, removePoint, setMissingValue, setErrorMode, setFilterFrom, setFilterTo, reassignColumn, setCustomFunctions, changeDiscretePointsOrder, setScoringTemplateState, } = scoringTemplateSlice.actions;

function assignScoringTemplate(service, templateId) {
    return async (dispatch, getState) => {
        const state = getState();
        const tableId = selectTableId(state);
        console.info('assign scoring template', templateId, ' to table', tableId);
        try {
            const assignResponse = await service.assignScoringTemplate(tableId, templateId);
            console.info('COMPLETE assign scoring template', assignResponse);
        }
        catch (error) {
            console.error(error);
        }
    };
}
function unassignScoringTemplate(service) {
    return async (dispatch, getState) => {
        const state = getState();
        const tableId = selectTableId(state);
        console.info('unassign scoring template to table', tableId);
        try {
            const assignResponse = await service.assignScoringTemplate(tableId, null);
            console.info('COMPLETE unassign scoring template', assignResponse);
        }
        catch (error) {
            console.error(error);
        }
    };
}
function fetchDesirabilityFunctions(service) {
    return async (dispatch) => {
        console.info('fetchDesirabilityFunctions');
        try {
            const { data: functions, total } = await service.getDesirabilityFunctionList();
            let allFunctions = [...functions];
            if (total > functions.length) {
                // it's the case, when we have more custom functions than initial requested limit
                const newLimit = total - functions.length;
                const newSkip = functions.length;
                const { data: restFunctions } = await service.getDesirabilityFunctionList({
                    skip: newSkip,
                    limit: newLimit,
                });
                allFunctions = [...functions, ...restFunctions];
            }
            dispatch(setCustomFunctions({ functions: allFunctions }));
            console.info('COMPLETE fetchDesirabilityFunctions', functions.length);
        }
        catch (error) {
            console.error(error);
        }
    };
}
function getPayloadForSavingTemplate(getState, name) {
    const state = getState();
    const template = selectScoringTemplate(state);
    return scoringFunctionToPayload(name, template);
}
async function afterSavingTemplate(dispatch, service, assignedTemplateId) {
    await dispatch(assignScoringTemplate(service, assignedTemplateId));
    dispatch(templateSaved());
}
function createScoringTemplate(service, name, onSaveCallback) {
    return async (dispatch, getState) => {
        console.info('createScoringTemplate', name);
        try {
            const payload = getPayloadForSavingTemplate(getState, name);
            const response = await onSaveCallback({
                ...payload,
                name,
                columns: payload.columns ?? [],
            });
            console.info('COMPLETE save new scoring template', response);
            if (!response._id) {
                console.error(`Response doesn't contain '_id' field. Attention!`);
                return;
            }
            const parentId = response.parentId || response._id;
            dispatch(setTableScoringTemplateId({ parentId: parentId, id: response._id }));
            const assignedTemplateId = response._id;
            await afterSavingTemplate(dispatch, service, assignedTemplateId);
        }
        catch (error) {
            console.error(error);
            // let the app handle error
            throw error;
        }
    };
}
function updateScoringTemplate(service, template, onUpdateCallback) {
    return async (dispatch, getState) => {
        try {
            const payload = getPayloadForSavingTemplate(getState, template.name);
            const response = await onUpdateCallback({
                ...template,
                ...payload,
                name: template.name,
            });
            if (!response._id) {
                console.error(`Response doesn't contain '_id' field. Attention!`);
                return;
            }
            const assignedTemplateId = response._id;
            await afterSavingTemplate(dispatch, service, assignedTemplateId);
        }
        catch (error) {
            console.error(error);
            // let the app handle error
            throw error;
        }
    };
}
function createScoringTemplateVersion(service, onSaveCallback, parentTemplateId, name, versionName, description) {
    return async (dispatch, getState) => {
        try {
            const payload = getPayloadForSavingTemplate(getState, name);
            payload.parentId = parentTemplateId;
            payload.description = description;
            payload.versionName = `${versionName}`;
            const response = await onSaveCallback({
                ...payload,
                name,
                columns: payload.columns ?? [],
            });
            console.info('COMPLETE saving new version of scoring template', response);
            if (!response._id) {
                console.error(`Response doesn't contain '_id' field. Attention!`);
                return;
            }
            dispatch(setTableScoringTemplateId({ parentId: parentTemplateId, id: response._id }));
            const assignedTemplateId = response._id;
            await afterSavingTemplate(dispatch, service, assignedTemplateId);
        }
        catch (error) {
            console.error(error);
        }
    };
}

export { DEFAULT_TEMPLATE_SIZE, DesirabilityFunctionLineSerialization, addColumnToScoring, addPoint, assignScoringTemplate, changeColumnOrder, changeDesirabilityFunction, changeDesirabilityParams, changeDiscretePointsOrder, columnsToScoringFunctions, convertTemplateToTableDataType, createScoringTemplate, createScoringTemplateVersion, desirabilityFunctionToPayload, fetchDesirabilityFunctions, initFromTableMetadata, movePoint, reassignColumn, removeColumnFromScoring, removePoint, resetToDefault, saveDesirabilityFunction, scoringFunctionToPayload, scoringTemplateSlice, selectCustomFunctions, selectErrorsAvailable, selectFilterFrom, selectFilterTo, selectMeasurementError, selectMissingValue, selectMissingValueReplacementColumn, selectMissingValueY, selectPropertyPanelOpen, selectScoringColumnOrder, selectScoringFunction, selectScoringFunctionByColumn, selectScoringOpenPropertyPanels, selectScoringTemplate, selectScoringTemplateChanged, selectScoringTemplateData, selectScoringWeight, setColumnColor, setCustomFunctions, setErrorMode, setFilterFrom, setFilterTo, setFromSavedTemplate, setMissingValue, setScoringTemplateState, setScoringWeight, templateSaved, toggleColumnInScoring, toggleColumnPropertyOpen, unassignScoringTemplate, updateScoringTemplate };
//# sourceMappingURL=index.js.map
